import { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import {
  LayoutDashboard, FileUp, FileSearch, FileText, CheckSquare,
  GitBranch, FlaskConical, Shield, PanelLeftClose, PanelLeft, LogOut,
  ScrollText, Ship, Anchor, Navigation, DollarSign, FileBarChart,
  Leaf, Sparkles, TrendingUp, BarChart3, Users, Zap, Scale
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import NotificationPanel from '@/components/NotificationPanel';
import SLABreachBadge from '@/components/SLABreachBadge';

const NAV_SECTIONS = [
  {
    label: null,
    items: [
      { to: '/', label: 'Dashboard', icon: LayoutDashboard },
    ],
  },
  {
    label: 'Invoice Pipeline',
    items: [
      { to: '/invoices', label: 'Invoices', icon: FileText },
      { to: '/upload', label: 'Upload', icon: FileUp },
      { to: '/extraction-review', label: 'AI Review', icon: FileSearch },
      { to: '/approvals', label: 'Approvals', icon: CheckSquare },
    ],
  },
  {
    label: 'Trading & Matching',
    items: [
      { to: '/charter-parties', label: 'Charter Parties', icon: ScrollText },
      { to: '/movements', label: 'Movements', icon: Ship },
      { to: '/cost-attribution', label: 'Cost Attribution', icon: GitBranch },
      { to: '/molecule-cost', label: 'Molecule Cost', icon: FlaskConical },
    ],
  },
  {
    label: 'Cargo Scheduling',
    items: [
      { to: '/vessel-tracking', label: 'Vessel Tracking', icon: Navigation },
      { to: '/demurrage-forecast', label: 'Demurrage Forecast', icon: DollarSign },
      { to: '/berth-schedule', label: 'Berth Schedule', icon: Anchor },
    ],
  },
  {
    label: 'Contract Analytics',
    items: [
      { to: '/contracts', label: 'SPA Analytics', icon: FileBarChart },
    ],
  },
  {
    label: 'Emissions & AI',
    items: [
      { to: '/emissions', label: 'Emissions', icon: Leaf },
      { to: '/deal-analyzer', label: 'Deal Analyzer', icon: Zap },
      { to: '/deal-comparison', label: 'Deal Comparison', icon: Scale },
      { to: '/ask', label: 'Ask MolecuAI', icon: Sparkles },
    ],
  },
  {
    label: 'Executive Intelligence',
    items: [
      { to: '/executive', label: 'Executive Dashboard', icon: BarChart3 },
      { to: '/forecasting', label: 'Cost Forecasting', icon: TrendingUp },
      { to: '/benchmarking', label: 'Benchmarking', icon: Users },
    ],
  },
  {
    label: 'System',
    items: [
      { to: '/governance', label: 'AI Governance', icon: Shield },
    ],
  },
];

export default function Sidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <TooltipProvider delayDuration={0}>
      <aside
        data-testid="sidebar"
        className={`fixed top-0 left-0 z-40 h-screen bg-white border-r border-slate-200 flex flex-col transition-all duration-200 ${collapsed ? 'w-[52px]' : 'w-56'}`}
      >
        {/* Logo */}
        <div className={`flex items-center h-14 border-b border-slate-100 shrink-0 ${collapsed ? 'justify-center px-2' : 'px-4 gap-3'}`}>
          <div className="w-7 h-7 rounded bg-[#0A2463] flex items-center justify-center flex-shrink-0">
            <Anchor className="h-3.5 w-3.5 text-white stroke-[2px]" />
          </div>
          {!collapsed && (
            <>
              <span className="font-heading font-black text-base tracking-tighter text-slate-950 flex-1">
                MolecuAI
              </span>
              <SLABreachBadge />
              <NotificationPanel />
            </>
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto py-2">
          {NAV_SECTIONS.map((section, si) => (
            <div key={si} className={si > 0 ? 'mt-3' : ''}>
              {/* Section Label */}
              {section.label && !collapsed && (
                <p className="px-4 mb-1.5 text-[9px] uppercase tracking-[0.2em] font-bold text-slate-400/80 select-none">
                  {section.label}
                </p>
              )}
              {section.label && collapsed && (
                <div className="mx-2 my-2"><Separator className="bg-slate-100" /></div>
              )}

              {/* Items */}
              <div className="space-y-px px-2">
                {section.items.map(({ to, label, icon: Icon }) => (
                  <Tooltip key={to}>
                    <TooltipTrigger asChild>
                      <NavLink
                        to={to}
                        end={to === '/'}
                        data-testid={`nav-${label.toLowerCase().replace(/\s/g, '-')}`}
                        className={({ isActive }) => {
                          const base = 'group flex items-center rounded-md text-[13px] font-medium transition-all duration-150';
                          const sizing = collapsed ? 'justify-center h-9 w-full' : 'gap-2.5 px-2.5 h-8';
                          if (isActive) {
                            return `${base} ${sizing} bg-[#0A2463]/[0.06] text-[#0A2463]`;
                          }
                          return `${base} ${sizing} text-slate-500 hover:text-slate-800 hover:bg-slate-50`;
                        }}
                      >
                        <Icon className={`h-[15px] w-[15px] flex-shrink-0 stroke-[1.8px]`} />
                        {!collapsed && <span className="truncate">{label}</span>}
                      </NavLink>
                    </TooltipTrigger>
                    {collapsed && (
                      <TooltipContent side="right" sideOffset={6} className="rounded-md text-xs font-medium">
                        {label}
                      </TooltipContent>
                    )}
                  </Tooltip>
                ))}
              </div>
            </div>
          ))}
        </nav>

        {/* Footer */}
        <div className="border-t border-slate-100 shrink-0">
          {!collapsed && user && (
            <div className="flex items-center gap-2.5 px-4 py-3">
              <div className="w-7 h-7 rounded-md bg-[#0A2463]/10 flex items-center justify-center text-[11px] font-bold text-[#0A2463] flex-shrink-0">
                {user.full_name?.[0] || 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-slate-800 truncate leading-tight">{user.full_name}</p>
                <p className="text-[10px] uppercase tracking-[0.12em] text-slate-400 leading-tight">{user.role}</p>
              </div>
            </div>
          )}
          <div className={`flex ${collapsed ? 'flex-col items-center py-2' : 'items-center justify-between px-3 pb-3'} gap-0.5`}>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              data-testid="logout-button"
              className="text-slate-400 hover:text-red-600 hover:bg-red-50/50 rounded-md h-7"
            >
              <LogOut className="h-3.5 w-3.5" />
              {!collapsed && <span className="ml-1.5 text-[11px]">Sign out</span>}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setCollapsed(!collapsed)}
              data-testid="sidebar-toggle"
              className="text-slate-300 hover:text-slate-500 rounded-md h-7 w-7"
            >
              {collapsed ? <PanelLeft className="h-3.5 w-3.5" /> : <PanelLeftClose className="h-3.5 w-3.5" />}
            </Button>
          </div>
          {!collapsed && (
            <div className="px-3 pb-3 pt-1 border-t border-slate-100" data-testid="powered-by-sidebar">
              <p className="text-[9px] uppercase tracking-[0.15em] text-slate-400 mb-1.5 text-center">Powered by</p>
              <div className="flex items-center justify-center gap-2.5">
                <img src="/junolabs-logo.png" alt="Juno Labs AI" className="h-7 object-contain" />
                <span className="text-[9px] text-slate-300">|</span>
                <img src="/rts-logo.png" alt="Resolve Tech Solutions" className="h-5 object-contain" />
              </div>
            </div>
          )}
        </div>
      </aside>
    </TooltipProvider>
  );
}
